import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, OtpInput } from '@/shared/ui';

import { FormError } from '@/shared/ui';

export interface TotpCodeFormProps {
  /** Resolves when the code has been handled. Rejecting is the caller's error path. */
  onSubmit: (code: string) => void;
  submitting: boolean;
  /** Already-translated message. Non-empty puts the field in the error state. */
  error?: string | undefined;
  /**
   * Bumped by the caller on every failure. Watching a counter rather than
   * `error` means two identical failures in a row still clear the field —
   * `error` alone would not change and the stale code would sit there.
   */
  errorNonce?: number;
  submitLabel?: ReactNode;
  disabled?: boolean;
  autoFocus?: boolean;
  /** Rendered between the boxes and the button — countdown, hints, alt paths. */
  children?: ReactNode;
}

/**
 * Six-digit TOTP entry plus a submit button.
 *
 * Used by the sign-in challenge, the 2FA setup confirmation, the disable flow
 * and the step-up dialog. WalletFront had two near-identical copies of this and
 * a third inline in the profile module; there is one here.
 *
 * The behaviours that matter:
 *
 *  - **The field is cleared and refocused after every failure.** TOTP codes are
 *    single-use: leaving a spent code in the boxes invites the user to press
 *    submit again and collect `2fa_code_already_used`, which reads like the app
 *    is broken. Clearing is the correct affordance, not a nicety.
 *  - Auto-submit fires when the sixth digit lands, so the common path needs no
 *    button press. The button stays for keyboard and screen-reader users and for
 *    retrying after a failure.
 *  - Auto-submit is suppressed while `submitting`, so a paste that completes the
 *    code during an in-flight request cannot fire a second one.
 */
export function TotpCodeForm({
  onSubmit,
  submitting,
  error,
  errorNonce = 0,
  submitLabel,
  disabled = false,
  autoFocus = true,
  children
}: TotpCodeFormProps) {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const containerRef = useRef<HTMLFormElement>(null);
  const lastNonceRef = useRef(errorNonce);

  useEffect(() => {
    if (errorNonce === lastNonceRef.current) return;
    lastNonceRef.current = errorNonce;

    setCode('');
    // Focus the first box so the user can just type the next code.
    containerRef.current?.querySelector('input')?.focus();
  }, [errorNonce]);

  const canSubmit = code.length === 6 && !submitting && !disabled;

  const submit = (value: string) => {
    if (submitting || disabled) return;
    onSubmit(value);
  };

  return (
    <form
      ref={containerRef}
      noValidate
      onSubmit={(event: FormEvent) => {
        event.preventDefault();
        if (canSubmit) submit(code);
      }}
      className="flex flex-col gap-4"
    >
      <OtpInput
        value={code}
        onChange={setCode}
        onComplete={submit}
        disabled={submitting || disabled}
        error={Boolean(error)}
        autoFocus={autoFocus}
        label={t('auth.totpTitle')}
      />

      <FormError message={error} />

      {children}

      <Button type="submit" fullWidth size="lg" loading={submitting} disabled={!canSubmit}>
        {submitLabel ?? t('auth.twoFactor.verify')}
      </Button>
    </form>
  );
}
